
import ProductListItemStructuredData from '@/components/ProductListItemStructuredData.vue'
import ProductListItemImages from '@/components/ProductListItemImages.vue'
import ProductListItemLabel from '@/components/ProductListItemLabel.vue'
import ProductStockStatus from '@/components/ProductStockStatus.vue'
import ProductListItemVariants from '@/components/ProductListItemVariants.vue'
import cartMixin from '@/mixins/cartMixin'
import commercialOperationPrice from '@/mixins/commercialOperationPrice'
import ProductContextMenu from '@/components/ProductContextMenu.vue'
import ProductListItemSpecs from '@/components/ProductListItemSpecs.vue'
import VehicleBulbBadge from '@/components/VehicleBulbBadge.vue'
import { openIntent } from '@/utils/openIntent'
import { formatSpecRange, variantSpecRange } from '@/utils/variantSpecRange'
import { writeProductCartContext } from '@/utils/productCartContextStorage'

export default {
  name: 'ProductListItem',
  components: {
    ProductListItemStructuredData,
    ProductListItemImages,
    ProductListItemLabel,
    ProductStockStatus,
    ProductListItemVariants,
    ProductContextMenu,
    ProductListItemSpecs,
    VehicleBulbBadge,
  },
  directives: { openIntent },
  mixins: [cartMixin, commercialOperationPrice],
  props: {
    product: Object,
    nameIsH2: {
      type: Boolean,
      default: true,
    },
    isFromSlider: {
      type: Boolean,
      default: false,
    },
    productMaxWidth: {
      type: [Number, null],
      default: null,
    },
    productIndex: {
      type: Number,
      default: -1,
    },
    cartMetadata: {
      type: Object,
      default: null,
    },
    displayMode: {
      type: String,
      default: 'classic',
    },
    enableEssentialSpecs: {
      type: Boolean,
      default: false,
    },
    badge: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      product_item: null,
      addingProductToCart: false,
      hoveredVariant: null,
      productHovered: false,
      selectedProduct: null,
      itemsContextMenu: [
        {
          label: 'components.productListItem.editProduct',
          link: this.urlToAdminPage(),
        },
      ],
    }
  },

  computed: {
    operationPrice() {
      return this.operationPriceOf(this.product_item)
    },
    isAdmin() {
      return (
        this.$auth.loggedIn && this.$auth.user?.role?.identifier === 'admin'
      )
    },
    visibilityIssues() {
      if (!this.isAdmin) return []

      const styles = {
        inactive: {
          label: 'components.productListItem.visibilityInactive',
          class: 'tw-text-orange-600 tw-bg-orange-100',
        },
        no_selectable_variant: {
          label: 'components.productListItem.visibilityNoVariant',
          class: 'tw-text-red-600 tw-bg-red-100',
        },
        pro_only: {
          label: 'components.productListItem.visibilityProOnly',
          class: 'tw-text-indigo-600 tw-bg-indigo-100',
        },
        unavailable: {
          label: 'components.productListItem.visibilityUnavailable',
          class: 'tw-text-gray-700 tw-bg-gray-200',
        },
      }

      return (this.product.customer_visibility_issues || [])
        .filter(issue => styles[issue])
        .map(issue => ({ key: issue, ...styles[issue] }))
    },
    isLcpImage() {
      if (this.productIndex < 0) {
        return false
      }
      return this.$device.isMobile
        ? this.productIndex === 0
        : this.productIndex < 4
    },
    cardBodyClass() {
      const baseClass =
        'w-100 d-flex justify-content-sm-between justify-content-end flex-row flex-sm-column position-relative tw-z-0'
      const hasVariants =
        this.product?.selectable_variants?.length > 1 &&
        !this.product?.use_alternative_variants_layout
      return hasVariants
        ? `${baseClass} align-items-start align-items-sm-center`
        : `${baseClass} align-items-center`
    },
    hasSeveralVariants() {
      return this.product.selectable_variants?.length > 1
    },
    showsProductSummary() {
      return this.hasSeveralVariants && !this.productHovered
    },
    displayedName() {
      return this.hasSeveralVariants && this.productHovered
        ? this.product_item.variant.name || this.product_item.name
        : this.product_item.name
    },
    specRanges() {
      const variants = this.showsProductSummary
        ? this.product.selectable_variants
        : [this.product_item.variant]
      return {
        watts: variantSpecRange(variants, 'watts'),
        lumens: variantSpecRange(variants, 'lumens'),
      }
    },
    getSlicedSpeficiations() {
      let specifications = []
      const watts = formatSpecRange(
        this.specRanges.watts,
        this.$device.isMobile ? ' W' : ' watts'
      )
      const lumens = formatSpecRange(
        this.specRanges.lumens,
        this.$device.isMobile ? ' lm' : ' lumens'
      )
      if (watts) specifications.push(watts)
      if (lumens) specifications.push(lumens)

      this.product_item.variant.specification_values_featured?.forEach(spec => {
        if (spec.is_featured) specifications.push(spec.value)
      })
      return specifications.slice(0, 2)
    },
    url() {
      return (
        this.$config.url +
        '/produit/' +
        this.product_item.slug +
        '?variant=' +
        this.product_item.variant.id
      )
    },
    productLink() {
      return this.localePath({
        name: 'produit-slug',
        params: { slug: this.product_item.slug },
        query:
          this.product_item.selectable_variants &&
          this.product_item.selectable_variants.length >= 2
            ? { variant: this.product_item.variant.id, fv: true }
            : {},
      })
    },
  },

  watch: {
    product(val) {
      this.product_item = { ...val }
    },
    hoveredVariant(val) {
      this.itemsContextMenu[0].link = this.urlToAdminPage()
      val
        ? (this.product_item.variant = val)
        : (this.product_item.variant = { ...this.product.variant })
    },
  },

  created() {
    this.product_item = { ...this.product }
  },

  methods: {
    handleContextMenu(event) {
      if (this.$auth.loggedIn && this.$auth.user.role.identifier === 'admin')
        this.$refs.contextMenu.showMenu(event)
    },
    urlToAdminPage() {
      return this.hoveredVariant
        ? `${this.$config.adminUrl}/produits/${this.product.id}?variantModal=${this.hoveredVariant.id}`
        : `${this.$config.adminUrl}/produits/${this.product.id}?variantModal=${this.product.variant.id}`
    },
    leaveProduct() {
      this.productHovered = false
      this.hoveredVariant = null
    },
    rememberCartContext() {
      writeProductCartContext(this.product.id, this.cartMetadata)
    },
    openProduct() {
      this.rememberCartContext()
      this.trackOperationBadgeProductClick(
        this.product_item,
        this.operationPrice
      )
    },
    goToOptions() {
      this.rememberCartContext()
      this.$router.push(
        this.localePath({
          name: 'produit-slug',
          params: { slug: this.product_item.slug },
        })
      )
    },
  },
}
